import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Code,
  Heading,
  HStack,
  Image,
  Input,
  Text,
  VStack,
  Badge,
  Divider,
  useClipboard,
  Alert,
  AlertIcon
} from "@chakra-ui/react";
import {
  getTwoFactorStatus,
  setupTwoFactor,
  verifyTwoFactorSetup,
  regenerateBackupCodes,
  TwoFactorStatus,
  TwoFactorSetupResponse
} from "api/webservice/twoFactor";
import { useToast } from "hooks";

export const TwoFactorSettings: React.FC = () => {
  const toast = useToast();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);

  // Setup state
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [setupCode, setSetupCode] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);

  // Backup codes state
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [regenCode, setRegenCode] = useState("");
  const [regenLoading, setRegenLoading] = useState(false);

  const { onCopy, hasCopied } = useClipboard(
    backupCodes ? backupCodes.join("\n") : ""
  );

  const fetchStatus = async () => {
    try {
      const result = await getTwoFactorStatus();
      setStatus(result);
    } catch {
      toast({
        title: "Error",
        description: "Failed to load 2FA status.",
        status: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSetup = async () => {
    setSetupLoading(true);
    try {
      const data = await setupTwoFactor();
      setSetupData(data);
      setBackupCodes(data.backupCodes);
    } catch (err: any) {
      toast({
        title: "Setup failed",
        description: err.response?.data?.response || "Failed to start 2FA setup.",
        status: "error"
      });
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!setupCode || setupCode.length !== 6) {
      toast({ title: "Invalid code", description: "Enter a 6-digit code.", status: "warning" });
      return;
    }

    setSetupLoading(true);
    try {
      await verifyTwoFactorSetup(setupCode);
      toast({ title: "Success", description: "2FA is now enabled.", status: "success" });
      setSetupData(null);
      setSetupCode("");
      fetchStatus();
    } catch (err: any) {
      toast({
        title: "Verification failed",
        description: err.response?.data?.response || "Invalid code.",
        status: "error"
      });
    } finally {
      setSetupLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!regenCode || regenCode.length !== 6) {
      toast({ title: "Invalid code", description: "Enter your current 6-digit code.", status: "warning" });
      return;
    }

    setRegenLoading(true);
    try {
      const result = await regenerateBackupCodes(regenCode);
      setBackupCodes(result.backupCodes);
      setRegenCode("");
      toast({ title: "Success", description: "New backup codes generated.", status: "success" });
    } catch (err: any) {
      toast({
        title: "Failed",
        description: err.response?.data?.response || "Failed to regenerate codes.",
        status: "error"
      });
    } finally {
      setRegenLoading(false);
    }
  };

  if (loading) {
    return <Text>Loading...</Text>;
  }

  return (
    <Box borderWidth="1px" borderRadius="lg" p={6}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="md">Two-Factor Authentication</Heading>
          <Badge colorScheme={status?.isEnabled ? "green" : "yellow"} fontSize="sm">
            {status?.isEnabled ? "Enabled" : "Setup Required"}
          </Badge>
        </HStack>

        <Text color="gray.300" fontSize="sm">
          Two-factor authentication is required for all accounts. Use an authenticator
          app to generate verification codes when signing in.
        </Text>

        <Divider />

        {/* SETUP FLOW */}
        {!status?.isEnabled && !setupData && (
          <VStack spacing={3} align="stretch">
            <Alert status="warning" borderRadius="md" bgColor="#FFFBEB">
              <AlertIcon />
              <Text color="#92400E">
                You must set up two-factor authentication to continue using your account.
              </Text>
            </Alert>
            <Button
              colorScheme="blue"
              onClick={handleSetup}
              isLoading={setupLoading}
            >
              Set Up Two-Factor Authentication
            </Button>
          </VStack>
        )}

        {setupData && (
          <VStack spacing={4} align="stretch">
            <Text fontWeight="bold">
              1. Scan this QR code with your authenticator app
            </Text>
            <Box textAlign="center">
              <Image
                src={setupData.qrCodeDataUrl}
                alt="2FA QR Code"
                maxW="200px"
                mx="auto"
              />
            </Box>

            <Text fontSize="sm" color="gray.500">
              Can&apos;t scan? Enter this key manually:{" "}
              <Code fontSize="xs">{setupData.secret}</Code>
            </Text>

            <Text fontWeight="bold" mt={4}>
              2. Enter the 6-digit code from your app to confirm
            </Text>
            <HStack>
              <Input
                placeholder="000000"
                value={setupCode}
                onChange={e => setSetupCode(e.target.value)}
                maxLength={6}
                maxW="150px"
              />
              <Button
                colorScheme="blue"
                onClick={handleVerifySetup}
                isLoading={setupLoading}
              >
                Verify & Enable
              </Button>
            </HStack>

            {backupCodes && (
              <Box mt={4}>
                <Alert status="warning" borderRadius="md" mb={3} bgColor="#FFFBEB">
                  <AlertIcon />
                  <Text color="#92400E">
                    Save these backup codes in a secure place. Each code can only be used once and can help you access your account if you&apos;re unable to use your authenticator app.
                  </Text>
                </Alert>
                <Box
                  bg="gray.500"
                  p={4}
                  borderRadius="md"
                  fontFamily="mono"
                  fontSize="sm"
                >
                  {backupCodes.map((code, i) => (
                    <Text key={i}>{code}</Text>
                  ))}
                </Box>
                <Button size="sm" mt={2} onClick={onCopy}>
                  {hasCopied ? "Copied!" : "Copy All Codes"}
                </Button>
              </Box>
            )}
          </VStack>
        )}

        {/* ENABLED STATE */}
        {status?.isEnabled && (
          <VStack spacing={4} align="stretch">
            <Text color="green.600" fontWeight="medium">
              ✓ Two-factor authentication is active on your account.
            </Text>

            {/* Regenerate backup codes */}
            <Box>
              <Text fontWeight="bold" mb={2}>Regenerate Backup Codes</Text>
              <Text fontSize="sm" color="gray.50" mb={2}>
                Enter your current authenticator code to generate new backup codes.
                This will invalidate your old ones. Each backup code can only be used once and can help you access your account if you&apos;re unable to use your authenticator app.
              </Text>
              <HStack>
                <Input
                  placeholder="000000"
                  value={regenCode}
                  onChange={e => setRegenCode(e.target.value)}
                  maxLength={6}
                  maxW="150px"
                />
                <Button
                  onClick={handleRegenerateBackupCodes}
                  isLoading={regenLoading}
                >
                  Regenerate
                </Button>
              </HStack>

              {backupCodes && (
                <Box mt={3}>
                  <Alert status="warning" borderRadius="md" mb={2} bgColor="#FFFBEB">
                    <AlertIcon />
                    <Text color="#92400E">
                      Save these new codes. Your old codes are now invalid.
                    </Text>
                  </Alert>
                  <Box
                    bg="gray.500"
                    p={4}
                    borderRadius="md"
                    fontFamily="mono"
                    fontSize="sm"
                  >
                    {backupCodes.map((code, i) => (
                      <Text key={i}>{code}</Text>
                    ))}
                  </Box>
                  <Button size="sm" mt={2} onClick={onCopy}>
                    {hasCopied ? "Copied!" : "Copy All Codes"}
                  </Button>
                </Box>
              )}
            </Box>
          </VStack>
        )}
      </VStack>
    </Box>
  );
};
